import React, { PropsWithoutRef, useEffect, useState } from 'react';
import DropDownPicker, { DropDownPickerProps, LanguageType } from 'react-native-dropdown-picker';
import { getDefaultLanguage } from '../services/translate';
import { ActivityIndicator, ColorValue, View } from 'react-native';
import defaultColors from '../services/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { WindowInformation } from '../services/window-information';
import { useStyles } from 'react-native-unistyles';
import MyAppText from './MyAppText';
import inputStylesheet from '../styles/inputStyles';

export type ValueType = string | number | boolean;

export default function MyDropDownPicker(props: PropsWithoutRef<DropDownPickerProps<ValueType>> & {
    borderColor?: ColorValue;
    placeholderColor?: ColorValue;
    height?: number;
    isLoading?: boolean;
    invalid?: boolean;
}) {
    const windowInfo = WindowInformation();
    const { theme } = useStyles();
    const [dropDownLanguage, setDropDownLanguage] = useState<LanguageType>('PT');
    const { styles: defaultInputStyle } = useStyles(inputStylesheet);
    const inputStyle = defaultInputStyle.input({ disabled: props.disabled, invalid: props.invalid });

    let height = inputStyle.height;
    if (props.height) {
        height = props.height;
    }

    useEffect(() => {
        (async () => {
            const language = await getDefaultLanguage();
            setDropDownLanguage(language.split('-')[0].toUpperCase() as LanguageType);
        })();
    }, []);

    return <DropDownPicker
        {...props}
        loading={props.isLoading}
        language={dropDownLanguage}
        badgeDotColors={defaultColors}
        searchWithRegionalAccents
        badgeTextStyle={{
            fontSize: inputStyle.fontSize,
            color: inputStyle.color,
            fontFamily: inputStyle.fontFamily,
            fontWeight: inputStyle.fontWeight,
        }}
        badgeColors={defaultInputStyle.input({ disabled: true }).backgroundColor}
        ListEmptyComponent={({
            listMessageContainer, listMessageTextStyle, message
        }) => {
            return <View style={[{ flex: 1, padding: 10, alignItems: 'center' }, listMessageContainer]}>
                {props.loading ? (
                    <ActivityIndicator color={theme.colors.loadingColor} />
                ) : (
                    <MyAppText style={listMessageTextStyle}>
                        {message}
                    </MyAppText>
                )}
            </View>;
        }}
        searchTextInputProps={{
            autoFocus: !windowInfo.isMobile,
        }}
        listItemContainerStyle={{
            flex: 1,
            padding: 5,
            minHeight: 32
        }}
        style={
            [props.style, {
                height: height,
                minHeight: height,
                borderWidth: 1,
                borderRadius: 4,
                flex: 1,
                borderColor: props.borderColor || inputStyle.borderColor,
                backgroundColor: inputStyle.backgroundColor,
            }]}
        placeholderStyle={{
            color: props.placeholderColor || defaultInputStyle.input({ placeholder: true }).color,
            fontSize: inputStyle.fontSize,
            fontWeight: defaultInputStyle.input({ placeholder: true }).fontWeight,
            fontFamily: inputStyle.fontFamily,
        }}
        textStyle={{
            fontSize: inputStyle.fontSize,
            color: inputStyle.color,
            fontFamily: inputStyle.fontFamily,
            fontWeight: inputStyle.fontWeight,
        }}
        dropDownContainerStyle={{
            borderColor: props.borderColor || inputStyle.borderColor,
            borderRadius: 4,
            backgroundColor: inputStyle.backgroundColor,
        }}
        searchTextInputStyle={{
            borderColor: props.borderColor || inputStyle.borderColor,
            borderRadius: 4,
            color: inputStyle.color,
        }}
        ArrowDownIconComponent={() => <FontAwesomeIcon icon={faChevronDown} color={inputStyle.color} />}
        ArrowUpIconComponent={() => <FontAwesomeIcon icon={faChevronUp} color={inputStyle.color} />}
        TickIconComponent={() => <FontAwesomeIcon icon={faCheck} color={inputStyle.color} />}
        searchContainerStyle={{
            borderBottomColor: props.borderColor || inputStyle.borderColor
        }} />;
}
